<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-2xl bg-gray-800 text-gray-300 rounded-lg p-4 sm:p-6 shadow-lg border border-gray-700 animate-scaleIn flex flex-col gap-4">

      <!-- Loading & Error -->
      <div v-if="isLoading" class="flex flex-col items-center py-6">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-red-600"></div>
        <p class="text-gray-300 text-sm mt-2">กำลังโหลดข้อมูล...</p>
      </div>

      <div v-else-if="error" class="flex flex-col gap-4">
        <div class="text-center text-red-300 py-6 text-sm">{{ error }}</div>
        <button
          @click="$emit('close')"
          class="bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600 flex items-center justify-center gap-2"
        >
          <i class="fas fa-times text-xs"></i> ปิด
        </button>
      </div>

      <!-- Main Content -->
      <div v-else class="flex flex-col gap-4">
        <!-- Tab: Home -->
        <div v-if="activeTab === 'home'" class="flex flex-col gap-4">
          <!-- Criminal Profile Card -->
          <div class="bg-gray-800 rounded-md p-3 border border-gray-700 transition-all duration-200 hover:border-indigo-600 hover:bg-gray-900">
            <div class="flex items-center gap-3">
              <div >
                <img
                  :src="criminal?.imageUrl || 'default-criminal.png'"
                  alt="Criminal"
                  class="w-16 h-16 rounded-full object-cover border-2 border-gray-600"
                />
                <div
                  class="absolute -bottom-1 -right-1 bg-indigo-600 text-white text-xxs sm:text-xs font-semibold rounded-full w-6 h-6 flex items-center justify-center border-2 border-gray-800"
                >
                  {{ criminal?.level || 'N/A' }}
                </div>
                <h3 class="text-gray-300 text-sm sm:text-lg font-semibold truncate text-center">
                  {{ criminal?.name || 'Unknown' }}
                </h3>
              </div>
              <div class="flex flex-col flex-1 min-w-0">
                
                <div class="mt-2 grid grid-cols-4 sm:grid-cols-7 gap-2 text-xxs sm:text-xs text-gray-300">
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700  col-span-2 sm:col-span-1">
                    <i class="fas fa-heart text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ criminalStats.maxHp - (criminal?.damageTaken || 0) }}/{{ criminalStats.maxHp }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-fist-raised text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ Math.floor(criminalStats.atk) }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-running text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ Math.floor(criminalStats.evasion) }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-crosshairs text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ Math.floor(criminalStats.critical) }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fa-solid fa-shield-virus text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ Math.floor(criminalStats.criticalReduction) }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-shield-alt text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ Math.floor(criminalStats.defense) }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fa-solid fa-person-rays text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ Math.floor(criminalStats.damageReduction) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Reward Section -->
          <div class="bg-gray-800 ">
            <p class="text-center text-gray-300 text-sm font-semibold mb-3 border-b border-gray-700 pb-2">
              รางวัลเมื่อลอบสังหารสำเร็จ
            </p>
            <div class="grid grid-cols-3 gap-3">
              <div
                v-if="criminal?.renownReward > 0"
                class="flex flex-col items-center bg-gray-800 p-3 rounded-md border border-gray-700"
              >
                <i class="fas fa-trophy text-yellow-400 text-lg mb-2"></i>
                <span class="text-xxs sm:text-xs text-gray-300 font-semibold">
                  ชื่อเสียง: {{ criminal.renownReward }}
                </span>
              </div>
              <div
                v-if="criminal?.silverReward > 0"
                class="flex flex-col items-center bg-gray-800 p-3 rounded-md border border-gray-700"
              >
                <i class="fas fa-coins text-gray-100 text-lg mb-2"></i>
                <span class="text-xxs sm:text-xs text-gray-300 font-semibold">
                  เหรียญเงิน: {{ criminal.silverReward }}
                </span>
              </div>
              <div
                v-if="criminal?.goldReward > 0"
                class="flex flex-col items-center bg-gray-800 p-3 rounded-md border border-gray-700"
              >
                <i class="fas fa-g text-yellow-400 text-lg mb-2"></i>
                <span class="text-xxs sm:text-xs text-gray-300 font-semibold">
                  ทอง: {{ criminal.goldReward }}
                </span>
              </div>
              <div
                v-if="criminal?.diamondReward > 0"
                class="flex flex-col items-center bg-gray-800 p-3 rounded-md border border-gray-700"
              >
                <i class="fas fa-gem text-blue-400 text-lg mb-2"></i>
                <span class="text-xxs sm:text-xs text-gray-300 font-semibold">
                  เพชร: {{ criminal.diamondReward }}
                </span>
              </div>
            </div>
          </div>

          <!-- Character Profile Card -->
          <div class="bg-gray-800 rounded-md p-3 border border-gray-700 transition-all duration-200 hover:border-indigo-600 hover:bg-gray-900">
            <div class="flex items-center gap-3">
              <div >
                <img
                  :src="character?.imageUrl || 'default-character.png'"
                  alt="Character"
                  class="w-16 h-16 rounded-full object-cover border-2 border-gray-600"
                />
                <div
                  class="absolute -bottom-1 -right-1 bg-indigo-600 text-white text-xxs sm:text-xs font-semibold rounded-full w-6 h-6 flex items-center justify-center border-2 border-gray-800"
                >
                  {{ characterPublic?.level || 'N/A' }}
                </div>
                <h3 class="text-gray-300 text-base sm:text-lg font-semibold truncate text-center">
                  {{ character?.characterName || 'Unknown' }}
                </h3>
              </div>
              <div class="flex flex-col flex-1 min-w-0">
                
                <div class="mt-2 grid grid-cols-4 sm:grid-cols-7 gap-2 text-xxs sm:text-xs text-gray-300">
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700 col-span-2 sm:col-span-1">
                    <i class="fas fa-heart text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ playerHp }}/{{ playerMaxHp }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-fist-raised text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ characterStats.atk }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-running text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ characterStats.evasion }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-crosshairs text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ characterStats.critical }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fa-solid fa-shield-virus text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ characterStats.criticalReduction }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fas fa-shield-alt text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ characterStats.defense }}</p>
                  </div>
                  <div class="text-center bg-gray-800 rounded p-1 border border-gray-700">
                    <i class="fa-solid fa-person-rays text-sm text-indigo-400"></i>
                    <p class="mt-0.5">{{ characterStats.damageReduction }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Buttons in Tab: Home -->
          <div class="flex flex-col gap-2">
            <button
              @click="activeTab = 'assassination'"
              class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-indigo-700"
              :disabled="isCriminalDead"
            >
              <i class="fas fa-user-ninja text-xs"></i> ดำเนินการลอบสังหาร
            </button>
            <button
              @click="$emit('close')"
              class="bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600 flex items-center justify-center gap-2"
            >
              <i class="fas fa-times text-xs"></i> ปิด
            </button>
          </div>
        </div>

        <!-- Tab: Assassination Menu -->
        <div v-else-if="activeTab === 'assassination'" class="flex flex-col gap-4">
          <!-- Dice Count Selection (VERTICAL, SMALLER) -->
          <div>
            <p class="text-gray-300 text-xs text-center mb-1">เลือกจำนวนลูกเต๋า</p>

            <div class="grid grid-cols-2 gap-1">
              <button
                v-for="opt in [1,2,3,4]"
                :key="opt"
                @click="selectDiceCount(opt)"
                :disabled="isDiceOptionDisabled(opt)"
                class="py-1 px-2 rounded-md text-xs text-white transition-colors duration-200 border relative"
                :class="diceOptionClass(opt)"
              >
                <div class="flex items-center gap-2">
                  <!-- Dice icons preview -->
                  <div class="flex items-center">
                    <i v-for="i in opt" :key="i" class="fas fa-dice-six text-xs mr-0.5 text-indigo-400"></i>
                  </div>

                  <div class="flex flex-col">
                    <span class="text-xs font-semibold text-gray-300">{{ opt }} ลูกเต๋า</span>
                    <span
                      class="text-xxs text-gray-300 opacity-80"
                      v-if="costFor(opt).amount === 0"
                    >
                      ฟรี
                    </span>
                    <span
                      class="text-xxs text-gray-300 opacity-80"
                      v-else
                    >
                      {{ costFor(opt).amount }}
                      <i :class="costFor(opt).icon + ' text-xxs ml-0.5'"></i>
                    </span>
                  </div>
                </div>

                <!-- Selected badge -->
                <span
                  v-if="diceCount === opt"
                  class="absolute top-0 right-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-600 text-white text-xxs border border-green-700"
                >
                  <i class="fas fa-check text-xxs"></i>
                </span>
              </button>
            </div>
          </div>

          <!-- Room Selection -->
          <div>
            <p class="text-gray-300 text-xs text-center mb-1">เลือกห้องที่อาชญากรซ่อนอยู่</p>
            <div class="grid grid-cols-5 gap-1">
              <button
                v-for="roomNum in Array.from({ length: criminal?.room || 1 }, (_, i) => i + 1)"
                :key="roomNum"
                @click="selectRoom(roomNum)"
                class="bg-gray-800 p-1 rounded-md border border-gray-700 hover:border-indigo-600 hover:bg-gray-900 transition-all duration-200"
                :class="{ 'border-2 border-indigo-600 bg-gray-900': selectedRoom === roomNum }"
                :disabled="isRolling || isCriminalDead"
              >
                <i class="fas fa-door-closed text-gray-300 text-xs"></i>
                <span class="block text-xxs font-semibold text-gray-300 mt-0.5">{{ roomNum }}</span>
              </button>
            </div>
          </div>

          <!-- Assassination Action -->
          <div>
            <!-- Dice Animation -->
            <div v-if="isRolling" class="text-center text-gray-300">
              <div class="dice-scene flex justify-center gap-2 mt-4 flex-wrap">
                <!-- Player Dice -->
                <div
                  v-for="(die, index) in tempPlayerDices"
                  :key="'player-' + index"
                  class="cube player-cube animate-dice-roll"
                >
                  <div class="face front">{{ die }}</div>
                  <div class="face back">{{ die }}</div>
                  <div class="face right">{{ die }}</div>
                  <div class="face left">{{ die }}</div>
                  <div class="face top">{{ die }}</div>
                  <div class="face bottom">{{ die }}</div>
                </div>
                <!-- Criminal Dice -->
                <div
                  v-for="(die, index) in tempCriminalDices"
                  :key="'criminal-' + index"
                  class="cube criminal-cube animate-dice-roll"
                  :class="{ 'delay-200': index % 2 === 1 }"
                >
                  <div class="face front">{{ die }}</div>
                  <div class="face back">{{ die }}</div>
                  <div class="face right">{{ die }}</div>
                  <div class="face left">{{ die }}</div>
                  <div class="face top">{{ die }}</div>
                  <div class="face bottom">{{ die }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Confirm and Back Buttons -->
          <div v-if="!isRolling" class="flex flex-col gap-2">
            <button
              @click="assassinate"
              :disabled="!selectedRoom || isRolling || isCriminalDead || isPlayerDead"
              class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 border border-indigo-700"
            >
              <i class="fas fa-user-ninja text-xs"></i>
              <span>
                ยืนยันลอบสังหาร {{ criminal?.name || 'Unknown' }} (50 <i class="fas fa-bolt text-yellow-400 text-xxs"></i>)
              </span>
            </button>
            <button
              @click="activeTab = 'home'"
              class="bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600 flex items-center justify-center gap-2"
            >
              <i class="fas fa-arrow-left text-xs"></i> ย้อนกลับ
            </button>
            <button
              v-if="isCriminalDead || isPlayerDead"
              @click="$emit('close')"
              class="bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600 flex items-center justify-center gap-2"
            >
              <i class="fas fa-times text-xs"></i> ปิด
            </button>
          </div>
        </div>

        <!-- Tab: Result -->
        <div v-else-if="activeTab === 'result'" class="flex flex-col gap-4">
          <!-- Not Encountered -->
          <div v-if="!isEncountered" class="text-center text-gray-300 py-6">
            <i class="fas fa-times-circle text-red-400 text-2xl mb-2"></i>
            <p class="text-sm">ไม่พบอาชญากรในห้องที่เลือก</p>
            <p class="text-xxs sm:text-xs text-gray-400 mt-1">{{ fightResult?.[0]?.message || 'ไม่มีข้อมูลผลลัพธ์' }}</p>
            <p class="text-xxs sm:text-xs text-yellow-400 mt-2 font-semibold">หมายเหตุ: อาชญากรจะเคลื่อนย้ายห้องเมื่อคุณเลือกห้องถูกต้องเท่านั้น</p>
          </div>

          <!-- Encountered -->
          <div v-else class="flex flex-col gap-4">
            <!-- Dice Results -->
            <div class="text-gray-300 text-center">
              <div v-if="playerDiceResults.length || criminalDiceResults.length" class="mb-4">
                <!-- Player Dice -->
                <div class="dice-scene flex justify-center items-center gap-2 flex-wrap mb-2">
                  <div
                    v-for="(die, index) in playerDiceResults"
                    :key="'player-result-' + index"
                    class="cube player-cube"
                  >
                    <div class="face front">{{ die }}</div>
                    <div class="face back">{{ die }}</div>
                    <div class="face right">{{ die }}</div>
                    <div class="face left">{{ die }}</div>
                    <div class="face top">{{ die }}</div>
                    <div class="face bottom">{{ die }}</div>
                  </div>
                  <span
                    v-if="playerDiceResults.length"
                    class="text-sm font-semibold bg-indigo-600 px-2 py-1 rounded-md border border-indigo-700"
                  >
                    รวม: {{ playerDiceResults.reduce((sum, die) => sum + die, 0) }}
                  </span>
                </div>
                <!-- Criminal Dice -->
                <div class="dice-scene flex justify-center items-center gap-2 flex-wrap">
                  <div
                    v-for="(die, index) in criminalDiceResults"
                    :key="'criminal-result-' + index"
                    class="cube criminal-cube"
                  >
                    <div class="face front">{{ die }}</div>
                    <div class="face back">{{ die }}</div>
                    <div class="face right">{{ die }}</div>
                    <div class="face left">{{ die }}</div>
                    <div class="face top">{{ die }}</div>
                    <div class="face bottom">{{ die }}</div>
                  </div>
                  <span
                    v-if="criminalDiceResults.length"
                    class="text-sm font-semibold bg-indigo-600 px-2 py-1 rounded-md border border-indigo-700"
                  >
                    รวม: {{ criminalDiceResults.reduce((sum, die) => sum + die, 0) }}
                  </span>
                </div>
              </div>

              <!-- Fight Events -->
              <div class="space-y-2 max-h-60 overflow-y-auto scrollbar-thin">
                <div
                  v-for="(event, index) in fightResult"
                  :key="index"
                  class="p-2 bg-gray-800 rounded-md border border-gray-700 animate-fadeIn"
                  :class="{
                    'border-l-4 border-indigo-400': event.type === 'dice',
                    'border-l-4 border-red-400': event.type === 'attack' && event.target === 'player',
                    'border-l-4 border-green-400': event.type === 'attack' && event.target === 'criminal',
                    'border-l-4 border-blue-400': event.type === 'reward',
                    'border-l-4 border-purple-400': event.type === 'status'
                  }"
                >
                  <!-- Dice Event -->
                  <div v-if="event.type === 'dice'" class="flex items-center gap-2">
                    <i class="fas fa-dice text-indigo-400"></i>
                    <span class="text-sm text-gray-300">{{ event.message }}</span>
                  </div>
                  <!-- Attack Event -->
                  <div v-if="event.type === 'attack'" class="flex items-center gap-2">
                    <i
                      :class="{
                        'fas fa-fist-raised': !event.isCritical && !event.isEvaded,
                        'fas fa-burst': event.isCritical,
                        'fas fa-shield-alt': event.isEvaded,
                        'text-red-400': event.target === 'player',
                        'text-green-400': event.target === 'criminal'
                      }"
                    ></i>
                    <span class="text-sm text-gray-300">{{ event.message }}</span>
                  </div>
                  <!-- Reward Event -->
                  <div v-if="event.type === 'reward'" class="flex items-center gap-2">
                    <i class="fas fa-gem text-blue-400"></i>
                    <span class="text-sm text-gray-300">{{ event.message }}</span>
                  </div>
                  <!-- Status Event -->
                  <div v-if="event.type === 'status'" class="flex items-center gap-2">
                    <i
                      :class="{
                        'fas fa-skull': event.status === 'death',
                        'fas fa-check': event.status === 'success',
                        'fas fa-times': event.status === 'failure' || event.status === 'neutral',
                        'text-purple-400': true
                      }"
                    ></i>
                    <span class="text-sm text-gray-300">{{ event.message }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- HP Status -->
            <p v-if="criminal" class="text-center text-gray-300 text-xs mt-3">
              HP ปัจจุบันของ {{ criminal?.name || 'Unknown' }}: {{ currentCriminalHp }}/{{ criminalStats.maxHp }}
            </p>
          </div>

          <!-- Close Button -->
          <div class="flex flex-col gap-2">
            <button
              v-if="!isCriminalDead && !isPlayerDead"
              @click="activeTab = 'assassination'; resetAssassination()"
              class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-indigo-700 flex items-center justify-center gap-2"
            >
              <i class="fas fa-redo text-xs"></i> ลองลอบสังหารใหม่
            </button>
            <button
              @click="$emit('close')"
              class="bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 border border-gray-600 flex items-center justify-center gap-2"
            >
              <i class="fas fa-times text-xs"></i> ปิด
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, onUnmounted, watch } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';

export default {
  name: 'AssassinationModal',
  props: {
    criminalId: {
      type: String,
      required: true,
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const criminal = ref(null);
    const criminalPrivate = ref(null);
    const character = ref(null);
    const characterPublic = ref(null);
    const isLoading = ref(true);
    const error = ref(null);
    const activeTab = ref('home');
    const selectedRoom = ref(null);
    const fightResult = ref([]);
    const playerDiceResults = ref([]);
    const criminalDiceResults = ref([]);
    const criminalDiceCount = ref(1);
    const isRolling = ref(false);
    const isEncountered = ref(false);
    const isCriminalDead = ref(false);
    const isPlayerDead = ref(false);
    const tempPlayerDices = ref([]);
    const tempCriminalDices = ref([]);
    const playerHp = ref(0);
    const playerMaxHp = ref(0);
    const diceCount = ref(1);
    const currentCriminalHp = ref(null);

    let diceInterval = null;
    let unsubscribeCriminal = null;

    const fetchCriminal = async () => {
      try {
        const doc = await projectFirestore.collection('criminals').doc(props.criminalId).get();
        if (doc.exists) {
          criminal.value = { id: doc.id, ...doc.data() };
          currentCriminalHp.value = criminalStats.value.maxHp - (criminal.value?.damageTaken || 0);
        } else {
          if (criminal.value) {
            criminal.value.damageTaken = criminalStats.value.maxHp;
            currentCriminalHp.value = 0;
            isCriminalDead.value = true;
          } else {
            error.value = 'ไม่พบข้อมูลของอาชญากรนี้';
          }
        }
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลอาชญากร: ' + err.message;
        console.error('fetchCriminal error:', err);
      }
    };

    const setupCriminalListener = () => {
      unsubscribeCriminal = projectFirestore
        .collection('criminals')
        .doc(props.criminalId)
        .onSnapshot(
          (doc) => {
            if (doc.exists) {
              criminal.value = { id: doc.id, ...doc.data() };
              currentCriminalHp.value = criminalStats.value.maxHp - (criminal.value?.damageTaken || 0);
              isCriminalDead.value = false;
            } else if (criminal.value) {
              criminal.value.damageTaken = criminalStats.value.maxHp;
              currentCriminalHp.value = 0;
              isCriminalDead.value = true;
              criminalPrivate.value = null;
            }
          },
          (err) => {
            console.error('Criminal listener error:', err);
            error.value = 'เกิดข้อผิดพลาดในการอัปเดตข้อมูลอาชญากรแบบเรียลไทม์: ' + err.message;
          }
        );
    };

    const fetchCriminalPrivate = async () => {
      try {
        const doc = await projectFirestore.collection('criminalPrivate').doc(props.criminalId).get();
        if (doc.exists) {
          criminalPrivate.value = doc.data();
        } else {
          criminalPrivate.value = null;
        }
      } catch (err) {
        console.error('fetchCriminalPrivate error:', err);
        criminalPrivate.value = null;
      }
    };

    watch(() => playerStore.characterPrivateData, (data) => {
      if (!data) {
        character.value = null;
        playerMaxHp.value = 0;
        playerHp.value = 0;
        return;
      }
      character.value = data;
      const stamina =
        (data.itemStats?.stamina || 0) +
        (data.bonusStats?.stamina || 0) +
        (data.stats?.stamina || 0) +
        (data.traitStats?.stamina || 0) +
        (data.skillStats?.stamina || 0) +
        (data.titleStats?.stamina || 0) +
        (data.tattooStats?.stamina || 0) +
        (data.ghostStats?.stamina || 0);
      const flatMaxHp =
        (data.itemStats?.maxHp || 0) +
        (data.bonusStats?.maxHp || 0) +
        (data.stats?.maxHp || 0) +
        (data.traitStats?.maxHp || 0) +
        (data.skillStats?.maxHp || 0) +
        (data.titleStats?.maxHp || 0) +
        (data.tattooStats?.maxHp || 0) +
        (data.ghostStats?.maxHp || 0);
      playerMaxHp.value = (stamina * 20 + flatMaxHp) || 0;
      playerHp.value = Math.max(0, playerMaxHp.value - (data.damageTaken || 0)) || 0;
    }, { immediate: true });

    watch(() => playerStore.characterData, (data) => {
      if (data) {
        characterPublic.value = data;
      } else {
        characterPublic.value = null;
      }
    }, { immediate: true });

    const characterStats = computed(() => {
      if (!character.value) {
        return { maxHp: 0, atk: 0, evasion: 0, critical: 0, criticalReduction: 0, defense: 0, damageReduction: 0, flee: 0 };
      }
      const str =
        (character.value.itemStats?.str || 0) +
        (character.value.bonusStats?.str || 0) +
        (character.value.stats?.str || 0) +
        (character.value.traitStats?.str || 0) +
        (character.value.skillStats?.str || 0) +
        (character.value.titleStats?.str || 0) +
        (character.value.tattooStats?.str || 0) +
        (character.value.ghostStats?.str || 0);
      const stamina =
        (character.value.itemStats?.stamina || 0) +
        (character.value.bonusStats?.stamina || 0) +
        (character.value.stats?.stamina || 0) +
        (character.value.traitStats?.stamina || 0) +
        (character.value.skillStats?.stamina || 0) +
        (character.value.titleStats?.stamina || 0) +
        (character.value.tattooStats?.stamina || 0) +
        (character.value.ghostStats?.stamina || 0);
      const int =
        (character.value.itemStats?.int || 0) +
        (character.value.bonusStats?.int || 0) +
        (character.value.stats?.int || 0) +
        (character.value.traitStats?.int || 0) +
        (character.value.skillStats?.int || 0) +
        (character.value.titleStats?.int || 0) +
        (character.value.tattooStats?.int || 0) +
        (character.value.ghostStats?.int || 0);
      const agi =
        (character.value.itemStats?.agi || 0) +
        (character.value.bonusStats?.agi || 0) +
        (character.value.stats?.agi || 0) +
        (character.value.traitStats?.agi || 0) +
        (character.value.skillStats?.agi || 0) +
        (character.value.titleStats?.agi || 0) +
        (character.value.tattooStats?.agi || 0) +
        (character.value.ghostStats?.agi || 0);
      const luck =
        (character.value.itemStats?.luck || 0) +
        (character.value.bonusStats?.luck || 0) +
        (character.value.stats?.luck || 0) +
        (character.value.traitStats?.luck || 0) +
        (character.value.skillStats?.luck || 0) +
        (character.value.titleStats?.luck || 0) +
        (character.value.tattooStats?.luck || 0) +
        (character.value.ghostStats?.luck || 0);
      const defense =
        (character.value.itemStats?.defense || 0) +
        (character.value.bonusStats?.defense || 0) +
        (character.value.stats?.defense || 0) +
        (character.value.traitStats?.defense || 0) +
        (character.value.skillStats?.defense || 0) +
        (character.value.titleStats?.defense || 0) +
        (character.value.tattooStats?.defense || 0) +
        (character.value.ghostStats?.defense || 0);
      const flatMaxHp =
        (character.value.itemStats?.maxHp || 0) +
        (character.value.bonusStats?.maxHp || 0) +
        (character.value.stats?.maxHp || 0) +
        (character.value.traitStats?.maxHp || 0) +
        (character.value.skillStats?.maxHp || 0) +
        (character.value.titleStats?.maxHp || 0) +
        (character.value.tattooStats?.maxHp || 0) +
        (character.value.ghostStats?.maxHp || 0);
      const flatAtk =
        (character.value.itemStats?.atk || 0) +
        (character.value.bonusStats?.atk || 0) +
        (character.value.stats?.atk || 0) +
        (character.value.traitStats?.atk || 0) +
        (character.value.skillStats?.atk || 0) +
        (character.value.titleStats?.atk || 0) +
        (character.value.tattooStats?.atk || 0) +
        (character.value.ghostStats?.atk || 0);
      const flatCritical =
        (character.value.itemStats?.critical || 0) +
        (character.value.bonusStats?.critical || 0) +
        (character.value.stats?.critical || 0) +
        (character.value.traitStats?.critical || 0) +
        (character.value.skillStats?.critical || 0) +
        (character.value.titleStats?.critical || 0) +
        (character.value.tattooStats?.critical || 0) +
        (character.value.ghostStats?.critical || 0);
      const flatEvasion =
        (character.value.itemStats?.evasion || 0) +
        (character.value.bonusStats?.evasion || 0) +
        (character.value.stats?.evasion || 0) +
        (character.value.traitStats?.evasion || 0) +
        (character.value.skillStats?.evasion || 0) +
        (character.value.titleStats?.evasion || 0) +
        (character.value.tattooStats?.evasion || 0) +
        (character.value.ghostStats?.evasion || 0);
      const defenseBonus = Math.floor(defense / 20);
      return {
        maxHp: stamina * 20 + flatMaxHp,
        atk: str * 2 + flatAtk,
        critical: Math.floor((int / 2) + flatCritical),
        evasion: Math.floor((agi / 2) + flatEvasion),
        criticalReduction: Math.floor(luck) + defenseBonus,
        defense: defense,
        damageReduction: defenseBonus,
        flee: Math.floor((agi + int) / 2),
      };
    });

    const criminalStats = computed(() => {
      if (!criminal.value || !criminal.value.stats) {
        return { maxHp: 0, atk: 0, evasion: 0, critical: 0, criticalReduction: 0, defense: 0, damageReduction: 0, flee: 0 };
      }
      const stats = criminal.value.stats;
      const defense = stats.defense || 0;
      const defenseBonus = Math.floor(defense / 20);
      return {
        atk: (stats.str || 0) * 2 + (stats.atk || 0),
        critical: Math.floor((stats.int || 0) / 2) + (stats.critical || 0),
        maxHp: (stats.stamina || 0) * 20 + (stats.maxHp || 0),
        evasion: Math.floor((stats.agi || 0) / 2) + (stats.evasion || 0),
        criticalReduction: Math.floor(stats.luck || 0) + defenseBonus,
        defense: defense,
        damageReduction: defenseBonus,
        flee: Math.floor(((stats.agi || 0) + (stats.int || 0)) / 2),
      };
    });

    const selectRoom = (roomNumber) => {
      if (!isRolling.value) {
        selectedRoom.value = roomNumber;
      }
    };

    const selectDiceCount = (count) => {
      if (!isRolling.value) {
        diceCount.value = count;
      }
    };

    const resetAssassination = () => {
      selectedRoom.value = null;
      fightResult.value = [];
      playerDiceResults.value = [];
      criminalDiceResults.value = [];
      criminalDiceCount.value = 1;
      isEncountered.value = false;
      tempPlayerDices.value = [];
      tempCriminalDices.value = [];
    };

    // === Helpers for ONE-ROW dice options ===
    const isDiceOptionDisabled = () => {
      return isRolling.value || isCriminalDead.value || isPlayerDead.value;
    };

    const diceOptionClass = (count) => {
      const base = 'relative flex-1 py-2 px-3 rounded-md border text-white text-left transition-all duration-200';
      const selected = diceCount.value === count
        ? 'bg-green-700 border-green-800 shadow'
        : 'bg-gray-800 hover:bg-gray-700 border-gray-700';
      const disabled = isDiceOptionDisabled(count) ? 'opacity-60 cursor-not-allowed' : '';
      return `${base} ${selected} ${disabled}`;
    };

    const costFor = (count) => {
      if (count === 1) return { amount: 0, icon: '' };
      if (count === 2) return { amount: 50, icon: 'fa-solid fa-coins text-gray-100' };
      if (count === 3) return { amount: 100, icon: 'fa-solid fa-coins text-gray-100' };
      if (count === 4) return { amount: 500, icon: 'fa-solid fa-coins text-gray-100' };
      return { amount: 0, icon: '' };
    };
    // ========================================

    const startDiceAnimation = () => {
      isRolling.value = true;
      tempPlayerDices.value = Array.from({ length: diceCount.value }, () => Math.floor(Math.random() * 6) + 1);
      tempCriminalDices.value = Array.from({ length: criminalDiceCount.value }, () => Math.floor(Math.random() * 6) + 1);
      diceInterval = setInterval(() => {
        tempPlayerDices.value = Array.from({ length: diceCount.value }, () => Math.floor(Math.random() * 6) + 1);
        tempCriminalDices.value = Array.from({ length: criminalDiceCount.value }, () => Math.floor(Math.random() * 6) + 1);
      }, 100);
    };

    const stopDiceAnimation = () => {
      if (diceInterval) {
        clearInterval(diceInterval);
        diceInterval = null;
      }
      isRolling.value = false;
    };

    const checkAssassinateCriminalId = () => {
      if (!user.value) return;
      if (playerStore.characterPrivateData && fightResult.value.length > 0) {
        activeTab.value = 'result';
      } else {
        activeTab.value = 'home';
      }
    };

    const assassinate = async () => {
      if (!selectedRoom.value) {
        error.value = 'กรุณาเลือกห้องก่อนดำเนินการลอบสังหาร';
        return;
      }
      try {
        startDiceAnimation();
        await new Promise((resolve) => setTimeout(resolve, 3000));
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const response = await gameActions({
          action: 'assassinateCriminal',
          criminalId: props.criminalId,
          selectedRoom: selectedRoom.value,
          diceCount: diceCount.value,
        });
        criminalDiceCount.value = response.data.criminalDiceCount || 1;
        tempCriminalDices.value = Array.from({ length: criminalDiceCount.value }, () => Math.floor(Math.random() * 6) + 1);
        stopDiceAnimation();
        fightResult.value = response.data.fightResult || [];
        playerDiceResults.value = response.data.playerDiceResults || [];
        criminalDiceResults.value = response.data.criminalDiceResults || [];
        isEncountered.value = response.data.isEncountered || false;
        isCriminalDead.value = response.data.isCriminalDead || false;
        isPlayerDead.value = response.data.isPlayerDead || false;
        currentCriminalHp.value = response.data.currentCriminalHp ?? 0;
        if (criminal.value && !isCriminalDead.value) {
          criminal.value.assassinationAttempts = (criminal.value.assassinationAttempts || 0) + 1;
          criminal.value.damageTaken = criminalStats.value.maxHp - (response.data.currentCriminalHp ?? criminalStats.value.maxHp);
        } else if (criminal.value && isCriminalDead.value) {
          criminal.value.damageTaken = criminalStats.value.maxHp;
          currentCriminalHp.value = 0;
        }
        if (!isCriminalDead.value) {
          await fetchCriminalPrivate();
        } else {
          criminalPrivate.value = null;
        }
        activeTab.value = 'result';
      } catch (err) {
        stopDiceAnimation();
        error.value = 'เกิดข้อผิดพลาดในการลอบสังหาร: ' + err.message;
        console.error('assassinate error:', err);
      }
    };

    onMounted(async () => {
      isLoading.value = true;
      if (user.value) {
        playerStore.init(user.value.uid);
      }
      try {
        await Promise.all([
          fetchCriminal(),
          fetchCriminalPrivate(),
        ]);
        setupCriminalListener();
        checkAssassinateCriminalId();
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลเริ่มต้น: ' + err.message;
        console.error('onMounted error:', err);
      } finally {
        isLoading.value = false;
      }
    });

    onUnmounted(() => {
      if (unsubscribeCriminal) {
        unsubscribeCriminal();
        unsubscribeCriminal = null;
      }
      stopDiceAnimation();
    });

    return {
      criminal,
      criminalPrivate,
      character,
      characterPublic,
      isLoading,
      error,
      activeTab,
      selectedRoom,
      selectRoom,
      selectDiceCount,
      diceCount,
      assassinate,
      fightResult,
      playerDiceResults,
      criminalDiceResults,
      criminalDiceCount,
      isRolling,
      isEncountered,
      isCriminalDead,
      isPlayerDead,
      tempPlayerDices,
      tempCriminalDices,
      playerHp,
      playerMaxHp,
      characterStats,
      criminalStats,
      resetAssassination,
      currentCriminalHp,
      // helpers
      isDiceOptionDisabled,
      diceOptionClass,
      costFor,
    };
  },
};
</script>

<style scoped>
.cube {
  position: relative;
  width: 40px;
  height: 40px;
  transform-style: preserve-3d;
  transition: transform 1s;
  margin: 0 auto;
  perspective: 800px;
}

.cube .face {
  position: absolute;
  width: 40px;
  height: 40px;
  line-height: 40px;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);
  backface-visibility: hidden;
}

.cube .face.front { transform: rotateY(0deg) translateZ(20px); }
.cube .face.back { transform: rotateY(180deg) translateZ(20px); }
.cube .face.right { transform: rotateY(90deg) translateZ(20px); }
.cube .face.left { transform: rotateY(-90deg) translateZ(20px); }
.cube .face.top { transform: rotateX(90deg) translateZ(20px); }
.cube .face.bottom { transform: rotateX(-90deg) translateZ(20px); }

.player-cube .face {
  background: linear-gradient(145deg, #3b82f6, #1e40af);
}
.criminal-cube .face {
  background: linear-gradient(145deg, #ef4444, #b91c1c);
}

@keyframes diceRoll {
  0% { transform: rotateX(0deg) rotateY(0deg); }
  100% { transform: rotateX(360deg) rotateY(360deg); }
}
.animate-dice-roll { animation: diceRoll 1s linear infinite; }
.delay-200 { animation-delay: 0.2s; }

.animate-spin { animation: spin 1s linear infinite; }
.animate-spin-pulse { animation: spin-pulse 1.2s ease-in-out infinite; }

@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes spin-pulse { 0% { transform: rotate(0deg) scale(1); } 50% { transform: rotate(180deg) scale(1.2); } 100% { transform: rotate(360deg) scale(1); } }

@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }

button:not(:disabled):active { transform: scale(0.98); }
button:disabled { opacity: 0.6; cursor: not-allowed; pointer-events: none; }

.scrollbar-thin::-webkit-scrollbar { width: 6px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #2d3748; border-radius: 3px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #4c51bf; border-radius: 3px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #5a67d8; }
</style>
